"use client";

import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { DeleteDialog } from "@/components/molecules/DeleteDialog";
import type { FormProps, ListItem } from "./types";
import { ListItemRow } from "./components";
import { useListActions } from "./hooks";
import { getTreeConfig } from "./utils";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Form5 = ({ selectedSection, onSave, variantDetails }: FormProps) => {
  const {
    listItems,
    editingItemId,
    componentFields,
    componentFieldErrors,
    componentFieldMessages,
    componentFieldsConfig,
    initializeData,
    initializeFormDefaults,
    handleFieldsUpdate,
    handleSave,
    handleEdit,
    handleDelete,
    handleNewItem,
  } = useListActions({ selectedSection, variantDetails, onSave });

  // Initialize data on section change
  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection?.componentId, selectedSection?.id]);

  // Initialize form defaults
  useEffect(() => {
    initializeFormDefaults();
  }, [initializeFormDefaults]);

  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ListItem | null>(null);

  if (!variantDetails) return null;

  // Get UI config
  const treeConfig = getTreeConfig(variantDetails.TREE_CONFIG);
  const { treeIcon, treeTitle, emptyMessage, primaryField, secondaryField, formLabels } = treeConfig;

  // Build delete confirmation message
  const getDeleteConfirmation = (item: ListItem): string => {
    if (treeConfig.deleteConfirmation) {
      return treeConfig.deleteConfirmation(item);
    }
    const itemName = item[primaryField] || item.name || item.id;
    return `Delete "${itemName}"?`;
  };

  // Delete handlers with confirmation
  const handleDeleteWithConfirm = (item: ListItem) => {
    setDeleteTarget(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget, getDeleteConfirmation(deleteTarget));
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* List Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <span>{treeIcon}</span> {treeTitle}
          </h3>
          <button
            onClick={handleNewItem}
            className="flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {formLabels.newButton}
          </button>
        </div>

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 max-h-64 overflow-y-auto">
          {listItems.length > 0 ? (
            <div className="space-y-1">
              {listItems.map((item, index) => (
                <ListItemRow
                  key={item.id || `item-${index}`}
                  item={item}
                  index={index}
                  isEditing={editingItemId === String(item.id)}
                  primaryField={primaryField}
                  secondaryField={secondaryField}
                  onEdit={handleEdit}
                  onDelete={handleDeleteWithConfirm}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">
              {emptyMessage}
            </div>
          )}
        </div>
      </div>

      {/* Form Section */}
      <div className="border-t pt-6">
        <h4 className="font-semibold mb-4">
          {editingItemId ? formLabels.edit : formLabels.addNew}
        </h4>

        <FieldsFormatter
          data={componentFields}
          fields={componentFieldsConfig}
          updateParent={true}
          onChange={handleFieldsUpdate}
          validationErrors={componentFieldErrors}
          validationMessages={componentFieldMessages}
          className="p-0 bg-transparent"
        />

        <div className="mt-6 flex gap-2">
          <Button onClick={handleSave} classType="admin" className="flex-1">
            {editingItemId ? formLabels.updateButton : formLabels.saveButton}
          </Button>
          {editingItemId && (
            <Button
              onClick={handleNewItem}
              classType="admin"
              className="px-4 bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              {formLabels.cancelButton}
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteDialog
          open={isDeleteDialogOpen}
          title="Delete Item"
          itemName={deleteTarget[primaryField] || deleteTarget.name || String(deleteTarget.id)}
          description={getDeleteConfirmation(deleteTarget)}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default Form5;

